// Kosten-Soll je Container (Plan 2026-10-18 P1, Nutzerentscheidung 18.10.2026), Geld-Pfad:
// Spedition + Zoll entstehen beim Anlegen des Containers als `erwartet`; der Misch-EK sperrt,
// solange ein Posten erwartet ist oder gar keiner existiert. Bestandscontainer bekommen die
// Posten hier per Knopf (Preisrecht); nur admin_general gibt mit Begruendung ohne weitere
// Kosten frei. Der Server prueft Rechte und Zustand selbst - die Knoepfe sind nur Fuehrung.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAddPurchaseCost, useAddStandardKosten, useGebeKostenFrei, type PurchaseCost } from "@/api/purchase";
import { fehlerText } from "@/lib/fehlerText";
import { parseBetrag } from "./board/zeilen";

export const KOSTEN_TYPEN = [["spedition", "Spedition"], ["zoll", "Zoll"], ["sonstig", "Sonstiges"]] as const;

/**
 * PATCH /purchase/costs/:id/ ersetzt den ganzen Posten: Typ und Bezeichnung bleiben, nur Betrag
 * und Status wechseln. „entfällt“ behaelt den alten Betrag (der Misch-EK zaehlt ihn als 0).
 */
export function kostenPatchBody(c: Pick<PurchaseCost, "typ" | "label">, status: string, amount_eur: number | null) {
  return { typ: c.typ, label: c.label, amount_eur, status };
}

/**
 * Neuen Kostenposten fuer einen Container anlegen. Ohne Betrag entsteht er als „erwartet“
 * (blockiert den Misch-EK, bis er erfasst ist), mit Betrag als „erfasst“.
 * `kompakt` = Formular in der Seitenspalte des Arbeitsplatzes (eigene Beschriftungen).
 */
export function NeuerKostenposten({ shipmentId, err, kompakt = false, onAbbrechen }: {
  shipmentId: number;
  err: (rueckfall: string) => (e: unknown) => void;
  kompakt?: boolean;
  onAbbrechen?: () => void;
}) {
  const add = useAddPurchaseCost();
  const [typ, setTyp] = useState("spedition");
  const [label, setLabel] = useState("");
  const [betrag, setBetrag] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const laeuft = useRef(false);

  function anlegen() {
    if (laeuft.current) return;
    const amount = parseBetrag(betrag);
    if (amount === undefined || (amount !== null && amount < 0)) return void setMeldung("Betrag nicht lesbar (z. B. 1.234,50)");
    setMeldung(null);
    laeuft.current = true;
    add.mutate(
      { shipmentId, body: { typ, label: label.trim() || null, amount_eur: amount, status: amount === null ? "erwartet" : "erfasst" } },
      {
        onSuccess: () => { setTyp("spedition"); setLabel(""); setBetrag(""); toast.success("Kosten-Posten angelegt"); },
        onError: err("Kosten-Posten fehlgeschlagen"),
        onSettled: () => { laeuft.current = false; },
      },
    );
  }

  const fehler = meldung ? <span role="alert" className="text-xs text-coral">{meldung}</span> : null;

  if (kompakt) {
    return (
      <div className="mt-2.5 grid gap-2 rounded-md border border-line bg-panel px-2.5 py-2">
        <label className="text-[11px] text-ink-2">
          Art des Kostenpostens
          <select
            value={typ}
            data-fokus-ziel="kosten"
            onChange={(e) => setTyp(e.target.value)}
            className="mt-1 h-8 w-full rounded border border-line bg-background px-2 text-xs"
          >
            {KOSTEN_TYPEN.map(([wert, text]) => <option key={wert} value={wert}>{text}</option>)}
          </select>
        </label>
        <label className="text-[11px] text-ink-2">
          Bezeichnung
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-8 w-full text-xs" />
        </label>
        <label className="text-[11px] text-ink-2">
          Betrag (EUR, leer = erwartet)
          <Input inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} className="mt-1 h-8 w-full text-xs" />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="gap-1.5" disabled={add.isPending} onClick={anlegen}>
            <Plus className="size-3.5" /> Posten anlegen
          </Button>
          {onAbbrechen ? <Button size="sm" variant="ghost" disabled={add.isPending} onClick={onAbbrechen}>Abbrechen</Button> : null}
          {fehler}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2">
      <select
        value={typ}
        onChange={(e) => setTyp(e.target.value)}
        className="rounded border border-line bg-background px-2 py-1 text-xs"
        aria-label={`Neuer Kosten-Typ Container ${shipmentId}`}
      >
        {KOSTEN_TYPEN.map(([wert, text]) => <option key={wert} value={wert}>{text}</option>)}
      </select>
      <Input placeholder="Bezeichnung" value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 w-40 text-xs" />
      <Input placeholder="EUR" inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} className="h-8 w-24 text-xs" />
      <Button size="sm" variant="outline" disabled={add.isPending} onClick={anlegen}>
        <Plus className="size-3.5" /> Posten
      </Button>
      {fehler}
    </div>
  );
}

export function KostenSollHinweis({ anzahlPosten }: { anzahlPosten: number }) {
  return (
    <>
      {anzahlPosten === 0 ? (
        <div className="text-xs text-ink-2">
          Noch keine Kosten-Posten. Ohne Spedition und Zoll bleibt der Misch-EK gesperrt.
        </div>
      ) : null}
      <p className="mt-1 text-[11px] text-ink-2">
        Einfuhrumsatzsteuer (EUSt) ist Vorsteuer — nur als Beleg ablegen, nicht als Kostenposten erfassen. Sie zählt nicht in den EK.
      </p>
    </>
  );
}

export function KostenSollAktionen({ shipmentId, costs, darfAnlegen, darfFreigeben }: {
  shipmentId: number;
  costs: ReadonlyArray<{ status: string }>;
  /** Preisrecht (article_purchase_prices). */
  darfAnlegen: boolean;
  /** admin_general. */
  darfFreigeben: boolean;
}) {
  const anlegen = useAddStandardKosten();
  const freigeben = useGebeKostenFrei();
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState("");

  const keinePosten = costs.length === 0;
  const offenePosten = costs.filter((c) => c.status === "erwartet").length;
  const zeigeAnlegen = darfAnlegen && keinePosten;
  const zeigeFreigabe = darfFreigeben && (keinePosten || offenePosten > 0);
  if (!zeigeAnlegen && !zeigeFreigabe) return null;

  const grundOk = grund.trim().length >= 3;

  return (
    <div className="mt-2 grid grid-cols-[minmax(0,1fr)] gap-2">
      <div className="flex flex-wrap gap-2">
        {zeigeAnlegen ? (
          <Button
            size="sm" variant="outline" disabled={anlegen.isPending}
            onClick={() => anlegen.mutate(shipmentId, {
              onSuccess: () => toast.success("Spedition und Zoll angelegt (erwartet)"),
              onError: (e) => toast.error(fehlerText(e, "Standard-Kosten konnten nicht angelegt werden")),
            })}
          >
            Spedition und Zoll anlegen
          </Button>
        ) : null}
        {zeigeFreigabe && !offen ? (
          <Button size="sm" variant="outline" onClick={() => setOffen(true)}>
            Ohne weitere Kosten freigeben…
          </Button>
        ) : null}
      </div>
      {zeigeFreigabe && offen ? (
        <div className="grid gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
          <span>
            {keinePosten
              ? "Spedition und Zoll werden als „entfällt“ angelegt."
              : `${offenePosten} ${offenePosten === 1 ? "Posten steht" : "Posten stehen"} auf „erwartet“. Ohne Betrag wird ein Posten „entfällt“ und zählt nicht in den EK; mit eingetragenem Betrag wird er „geschätzt“ und zählt weiter.`}
            {" "}Erfasste Beträge bleiben unverändert.
          </span>
          <label htmlFor={`kosten-freigabe-${shipmentId}`} className="font-medium">Begründung der Freigabe</label>
          <Textarea
            id={`kosten-freigabe-${shipmentId}`}
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="z. B. Spedition rechnet der Lieferant direkt ab"
            className="text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm" disabled={!grundOk || freigeben.isPending}
              onClick={() => freigeben.mutate({ shipmentId, grund: grund.trim() }, {
                onSuccess: () => { toast.success("Kosten freigegeben"); setOffen(false); setGrund(""); },
                onError: (e) => toast.error(fehlerText(e, "Freigabe fehlgeschlagen")),
              })}
            >
              Freigeben
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setOffen(false); setGrund(""); }}>Abbrechen</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
